/**
 * Animated GIF, written here rather than pulled in.
 *
 * A dependency would be the obvious answer, except that GIF is an indexed
 * format and these sprites are already indexed: a palette of names resolved to
 * a couple of dozen colours. Every general purpose encoder starts by quantising
 * millions of colours down to 256, which is work this format never needs and a
 * step that can only lose fidelity. Building the colour table straight from the
 * kit means the GIF is exact.
 *
 * It also keeps the promise the rest of the repo makes: no runtime
 * dependencies, and output that is byte for byte the same every time.
 */

import type { Bitmap } from "./png.js";

/** GIF allows 256 entries and one of them has to be the transparent slot. */
const MAX_COLOURS = 256;
/** Below this a pixel is treated as fully transparent rather than blended. */
const ALPHA_CUTOFF = 128;

export interface GifOptions {
	/** Milliseconds between frames. GIF stores hundredths, so this is rounded. */
	delayMs?: number;
	/** 0 means forever, which is what a sprite loop wants. */
	loops?: number;
}

interface Indexed {
	/** One palette index per pixel. */
	pixels: Uint8Array;
	table: number[][];
	transparent: number;
}

/**
 * One colour table for the whole animation.
 *
 * A per-frame table would be smaller but would let colours drift between
 * frames, which on a sprite reads as flickering. Sharing the table also means
 * the palette in the file is recognisably the sprite's own palette.
 */
function index(frames: Bitmap[]): Indexed | null {
	const table: number[][] = [[0, 0, 0]];
	const transparent = 0;
	const seen = new Map<number, number>();

	const total = frames.reduce((n, f) => n + f.width * f.height, 0);
	const pixels = new Uint8Array(total);

	let at = 0;
	for (const frame of frames) {
		for (let i = 0; i < frame.data.length; i += 4) {
			if (frame.data[i + 3] < ALPHA_CUTOFF) {
				pixels[at++] = transparent;
				continue;
			}
			const key = (frame.data[i] << 16) | (frame.data[i + 1] << 8) | frame.data[i + 2];
			let slot = seen.get(key);
			if (slot === undefined) {
				// More colours than GIF can hold. Refusing beats quietly posterising
				// somebody's sprite into something they did not draw.
				if (table.length >= MAX_COLOURS) return null;
				slot = table.length;
				table.push([frame.data[i], frame.data[i + 1], frame.data[i + 2]]);
				seen.set(key, slot);
			}
			pixels[at++] = slot;
		}
	}
	return { pixels, table, transparent };
}

/** Bytes go out in sub-blocks of at most 255, each prefixed with its length. */
function subBlocks(out: number[], bytes: number[]): void {
	for (let i = 0; i < bytes.length; i += 255) {
		const run = bytes.slice(i, i + 255);
		out.push(run.length, ...run);
	}
	out.push(0);
}

/**
 * LZW as GIF specifies it: codes are variable width, packed least significant
 * bit first, and the dictionary resets whenever it fills.
 */
function lzw(pixels: Uint8Array, minCodeSize: number): number[] {
	const clear = 1 << minCodeSize;
	const end = clear + 1;

	const bytes: number[] = [];
	let bitBuffer = 0;
	let bitCount = 0;
	const emit = (code: number, width: number) => {
		bitBuffer |= code << bitCount;
		bitCount += width;
		while (bitCount >= 8) {
			bytes.push(bitBuffer & 0xff);
			bitBuffer >>= 8;
			bitCount -= 8;
		}
	};

	let dict = new Map<number, number>();
	let next = end + 1;
	let width = minCodeSize + 1;
	const reset = () => {
		dict = new Map();
		next = end + 1;
		width = minCodeSize + 1;
	};

	emit(clear, width);
	let prefix = pixels.length ? pixels[0] : end;

	for (let i = 1; i < pixels.length; i++) {
		const c = pixels[i];
		const key = (prefix << 8) | c;
		const found = dict.get(key);
		if (found !== undefined) {
			prefix = found;
			continue;
		}
		emit(prefix, width);
		if (next < 4096) {
			dict.set(key, next++);
			// Widen only once the new code no longer fits, never before.
			if (next > 1 << width && width < 12) width++;
		} else {
			emit(clear, width);
			reset();
		}
		prefix = c;
	}

	emit(prefix, width);
	emit(end, width);
	if (bitCount > 0) bytes.push(bitBuffer & 0xff);
	return bytes;
}

const short = (n: number) => [n & 0xff, (n >> 8) & 0xff];

/**
 * Every frame must be the same size, which they are: they come from one spec.
 * Returns null if the animation needs more than 256 colours, so the caller can
 * say so rather than ship a wrong picture.
 */
export function encodeGif(frames: Bitmap[], options: GifOptions = {}): Uint8Array | null {
	if (!frames.length) return null;
	const { width, height } = frames[0];
	if (frames.some((f) => f.width !== width || f.height !== height)) return null;

	const indexed = index(frames);
	if (!indexed) return null;

	// The table has to be a power of two, padded with black.
	let bits = 1;
	while (1 << bits < indexed.table.length) bits++;
	const tableSize = 1 << bits;

	const out: number[] = [];
	out.push(...[0x47, 0x49, 0x46, 0x38, 0x39, 0x61]); // GIF89a

	// Logical screen descriptor: global table present, `bits` per pixel.
	out.push(...short(width), ...short(height), 0x80 | (bits - 1), indexed.transparent, 0);
	for (let i = 0; i < tableSize; i++) {
		const c = indexed.table[i] ?? [0, 0, 0];
		out.push(c[0], c[1], c[2]);
	}

	// Netscape extension, the only way to say "loop" in a GIF.
	const loops = options.loops ?? 0;
	out.push(0x21, 0xff, 0x0b, ...[..."NETSCAPE2.0"].map((ch) => ch.charCodeAt(0)), 0x03, 0x01, ...short(loops), 0);

	// GIF counts delay in hundredths of a second. Anything under two hundredths
	// is treated as "as fast as possible" by most viewers, which for a sprite
	// loop is far too fast to read, so it is floored.
	const delay = Math.max(2, Math.round((options.delayMs ?? 120) / 10));
	const minCodeSize = Math.max(2, bits);
	const perFrame = width * height;

	frames.forEach((_, i) => {
		// Graphic control: restore to background between frames, so a moving part
		// does not smear across the ones after it.
		out.push(0x21, 0xf9, 0x04, 0x09, ...short(delay), indexed.transparent, 0);
		out.push(0x2c, ...short(0), ...short(0), ...short(width), ...short(height), 0);
		out.push(minCodeSize);
		subBlocks(out, lzw(indexed.pixels.subarray(i * perFrame, (i + 1) * perFrame), minCodeSize));
	});

	out.push(0x3b);
	return Uint8Array.from(out);
}

/**
 * Every frame side by side at native size, which is what a game engine slices.
 *
 * No padding and no background: an engine is told the frame width and steps
 * along, so a gap would put a seam in every frame.
 */
export function spriteSheet(frames: Bitmap[]): Bitmap {
	const { width, height } = frames[0];
	const sheet = new Uint8Array(width * frames.length * height * 4);
	const rowBytes = width * frames.length * 4;

	frames.forEach((frame, n) => {
		for (let y = 0; y < height; y++) {
			const from = y * width * 4;
			const to = y * rowBytes + n * width * 4;
			sheet.set(frame.data.subarray(from, from + width * 4), to);
		}
	});

	return { width: width * frames.length, height, data: sheet };
}
